import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Card,
  CardContent,
  LinearProgress,
  IconButton,
  Tooltip,
  Badge,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControlLabel,
  Checkbox,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarColumnsButton
} from '@mui/x-data-grid';
import {
  Upload as UploadIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  CloudUpload as CloudUploadIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  Send as SendIcon,
  AssignmentReturn as AssignmentReturnIcon
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart as RechartsBarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import leadSubmissionsService from '../services/leadSubmissionsService';
import serviceProvidersService from '../services/serviceProvidersService';
import OpenedLeadsResubmission from '../components/OpenedLeadsResubmission';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const LeadSubmissions = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Dashboard Analytics State
  const [dashboardData, setDashboardData] = useState({
    overall: {},
    providers: [],
    dateRange: {}
  });
  
  // Submissions State
  const [submissions, setSubmissions] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 0
  });

  // Performance Metrics State
  const [performanceData, setPerformanceData] = useState([]);
  const [performancePeriod, setPerformancePeriod] = useState('daily');
  
  // Filters State
  const [filters, setFilters] = useState({
    status: '',
    feedback_status: '',
    provider_id: '',
    search: '',
    date_from: '',
    date_to: ''
  });

  // Service providers
  const [providers, setProviders] = useState([]);
  const [selectedProviders, setSelectedProviders] = useState([]);

  // Excel upload dialog
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProvider, setUploadProvider] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  useEffect(() => {
    fetchProviders();
  }, []);

  useEffect(() => {
    fetchDashboardAnalytics();
    if (activeTab === 1) {
      fetchSubmissions();
    } else if (activeTab === 2) {
      fetchPerformanceData();
    }
  }, [activeTab, filters, pagination.page, pagination.pageSize, selectedProviders, performancePeriod]);

  const fetchProviders = async () => {
    try {
      const response = await serviceProvidersService.getServiceProviders();
      setProviders(response.data);
    } catch (error) {
      console.error('Error fetching providers:', error);
    }
  };

  const fetchDashboardAnalytics = async () => {
    setLoading(true);
    try {
      const params = {
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value.length !== 0)
        )
      };
      
      if (selectedProviders.length > 0) {
        params.provider_ids = selectedProviders.join(',');
      }

      // Add date filters
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;

      const response = await leadSubmissionsService.getDashboardAnalytics(params);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '')
        )
      };

      const response = await leadSubmissionsService.getSubmissions(params);
      setSubmissions(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.pagination.total,
        pages: response.pagination.pages
      }));
    } catch (error) {
      console.error('Error fetching submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPerformanceData = async () => {
    setLoading(true);
    try {
      // Set different day ranges based on period
      let days;
      switch (performancePeriod) {
        case 'daily':
          days = 30;
          break;
        case 'weekly':
          days = 84; // 12 weeks
          break;
        case 'monthly':
          days = 365; // 12 months
          break;
        default:
          days = 30;
      }

      const params = {
        days: days,
        period: performancePeriod
      };
      
      // Add provider filter
      if (selectedProviders.length > 0) {
        params.provider_ids = selectedProviders.join(',');
      }

      // Add date filters from dashboard
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;

      console.log('Fetching performance data with params:', params);
      const response = await leadSubmissionsService.getProviderPerformance(params);
      console.log('Raw performance data response:', response);
      
      // Check if response exists and has data
      if (!response || !response.data) {
        console.warn('No response or response.data:', response);
        setPerformanceData([]);
        return;
      }

      // Handle different response structures
      let rawData = [];
      if (Array.isArray(response.data)) {
        rawData = response.data;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        rawData = response.data.data;
      } else {
        console.warn('Invalid performance data structure:', response);
        setPerformanceData([]);
        return;
      }

      console.log('Raw data array:', rawData);

      if (rawData.length === 0) {
        console.warn('No data returned from API');
        setPerformanceData([]);
        return;
      }

      // Aggregate data by date (sum across all providers for each date)
      const aggregatedData = {};
      rawData.forEach(item => {
        let dateKey;
        
        // Handle different date formats and periods
        if (performancePeriod === 'weekly') {
          // Group by week
          const date = new Date(item.date);
          const week = Math.floor(date.getDate() / 7) + 1;
          dateKey = `${date.getFullYear()}-W${week.toString().padStart(2, '0')}`;
        } else if (performancePeriod === 'monthly') {
          // Group by month
          const date = new Date(item.date);
          dateKey = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
        } else {
          // Daily - use date as is
          dateKey = item.date;
        }

        if (!aggregatedData[dateKey]) {
          aggregatedData[dateKey] = {
            date: dateKey,
            submissions: 0,
            successful: 0,
            conversions: 0
          };
        }
        
        aggregatedData[dateKey].submissions += parseInt(item.submissions || 0);
        aggregatedData[dateKey].successful += parseInt(item.successful || 0);
        aggregatedData[dateKey].conversions += parseInt(item.conversions || 0);
      });

      // Convert to array and sort by date
      const chartData = Object.values(aggregatedData).sort((a, b) => new Date(a.date) - new Date(b.date));
      
      console.log('Aggregated chart data:', chartData);
      console.log('Chart data length:', chartData.length);
      setPerformanceData(chartData);
    } catch (error) {
      console.error('Error fetching performance data:', error);
      console.error('Error details:', error.message, error.stack);
      setPerformanceData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleProviderSelection = (providerId) => {
    setSelectedProviders(prev => {
      if (prev.includes(providerId)) {
        return prev.filter(id => id !== providerId);
      } else {
        return [...prev, providerId];
      }
    });
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      feedback_status: '',
      provider_id: '',
      search: '',
      date_from: '',
      date_to: ''
    });
    setSelectedProviders([]);
  };

  const handleExcelUpload = async () => {
    if (!uploadFile || !uploadProvider) return;

    setUploadLoading(true);
    try {
      const result = await leadSubmissionsService.uploadExcelReport(uploadFile, uploadProvider);
      setUploadResult(result);
      fetchDashboardAnalytics(); // Refresh data
    } catch (error) {
      setUploadResult({
        success: false,
        error: error.message
      });
    } finally {
      setUploadLoading(false);
    }
  };

  const downloadReport = async (reportType, format = 'csv') => {
    try {
      const params = {
        report_type: reportType,
        format: format,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '' && value !== null)
        )
      };
      
      if (selectedProviders.length > 0) {
        params.provider_ids = selectedProviders.join(',');
      }

      // Add date filters
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;

      const response = await leadSubmissionsService.generateReport(params);
      
      if (format === 'csv') {
        // Handle CSV download
        const blob = new Blob([response], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${reportType}_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading report:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'success';
      case 'failed': return 'error';
      case 'pending': return 'warning';
      case 'sent': return 'info';
      default: return 'default';
    }
  };

  const getFeedbackStatusColor = (status) => {
    switch (status) {
      case 'qualified': return 'success';
      case 'converted': return 'info'; // Blue color for converted
      case 'rejected':
      case 'invalid': return 'error';
      case 'contacted': return 'info';
      case 'duplicate': return 'warning';
      case 'opened': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return <SuccessIcon fontSize="small" />;
      case 'failed': return <ErrorIcon fontSize="small" />;
      case 'pending': return <PendingIcon fontSize="small" />;
      case 'sent': return <SendIcon fontSize="small" />;
      default: return null;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleString();
  };

  // Dashboard Tab Content
  const DashboardTab = () => (
    <Box>
      {/* Provider Selection */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Service Provider Selection
        </Typography>
        <Grid container spacing={1}>
          {providers.map(provider => (
            <Grid item key={provider.id}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedProviders.includes(provider.id)}
                    onChange={() => handleProviderSelection(provider.id)}
                  />
                }
                label={provider?.name || 'Unknown Provider'}
              />
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Date Range Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Date Range Filter
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              label="From Date"
              type="date"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="To Date"
              type="date"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              onClick={() => {
                const today = new Date().toISOString().split('T')[0];
                const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
                handleFilterChange('date_from', thirtyDaysAgo);
                handleFilterChange('date_to', today);
              }}
              variant="outlined"
              size="small"
              fullWidth
            >
              Last 30 Days
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              onClick={() => {
                handleFilterChange('date_from', '');
                handleFilterChange('date_to', '');
              }}
              variant="outlined"
              size="small"
              fullWidth
            >
              Clear Dates
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Overall Statistics */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Submissions
              </Typography>
              <Typography variant="h4">
                {dashboardData.overall?.total || 0}
              </Typography>
              <Typography variant="body2" color="success.main">
                Success Rate: {dashboardData.overall?.successRate || 0}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Quotes Generated
              </Typography>
              <Typography variant="h4" color="info.main">
                {dashboardData.overall?.quotes || 0}
              </Typography>
              <Typography variant="body2">
                Submission to Quote Rate: {dashboardData.overall?.quoteRate || 0}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Quote Rate
              </Typography>
              <Typography variant="h4" color="warning.main">
                {dashboardData.overall?.quoteRate || 0}%
              </Typography>
              <Typography variant="body2">
                Submissions to Quotes
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Sales Converted
              </Typography>
              <Typography variant="h4" color="success.main">
                {dashboardData.overall?.sales || 0}
              </Typography>
              <Typography variant="body2">
                Conversion Rate: {dashboardData.overall?.conversionRate || 0}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Lead-to-Quote Ratio */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                OPENED Leads
              </Typography>
              <Typography variant="h4" color="default">
                {dashboardData.overall?.opened || 0}
              </Typography>
              <Typography variant="body2">
                Leads with no contact
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Lead-to-Quote Ratio
              </Typography>
              <Typography variant="h4" color={dashboardData.overall?.leadToQuoteRatio >= 25 ? 'success' : 'warning'}>
                {dashboardData.overall?.leadToQuoteRatio || 0}%
              </Typography>
              <Typography variant="body2">
                Target: 25% minimum
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Conversion Rate
              </Typography>
              <Typography variant="h4" color="info.main">
                {dashboardData.overall?.conversionRate || 0}%
              </Typography>
              <Typography variant="body2">
                Quotes to Sales
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Provider Performance Comparison */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Provider Performance Comparison
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <RechartsBarChart data={dashboardData.providers || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="provider.name" />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Bar dataKey="stats.total" fill="#8884d8" name="Total Submissions" />
                <Bar dataKey="stats.successful" fill="#82ca9d" name="Successful" />
                <Bar dataKey="stats.quotes" fill="#ffc658" name="Quotes" />
                <Bar dataKey="stats.sales" fill="#ff7300" name="Sales" />
              </RechartsBarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Success Rate by Provider
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <RechartsPieChart>
                <Pie
                  data={(dashboardData.providers || []).map(p => ({
                    name: p?.provider?.name || 'Unknown',
                    value: parseFloat(p?.stats?.successRate || 0)
                  }))}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({name, value}) => `${name}: ${value}%`}
                >
                  {(dashboardData.providers || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip />
              </RechartsPieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Provider Details Table */}
      <Paper sx={{ mt: 3, p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Detailed Provider Statistics
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Provider</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="right">Success Rate</TableCell>
                <TableCell align="right">OPENED</TableCell>
                <TableCell align="right">Quotes</TableCell>
                <TableCell align="right">Quote Rate</TableCell>
                <TableCell align="right">Sales</TableCell>
                <TableCell align="right">Conversion Rate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(dashboardData.providers || []).map((provider) => (
                <TableRow key={provider?.provider?.id || Math.random()}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {provider?.provider?.name || 'Unknown Provider'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {provider?.provider?.api_type || 'N/A'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">{provider?.stats?.total || 0}</TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={`${provider?.stats?.successRate || 0}%`}
                      color={parseFloat(provider?.stats?.successRate || 0) > 80 ? 'success' : 
                             parseFloat(provider?.stats?.successRate || 0) > 60 ? 'warning' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">{provider?.stats?.opened || 0}</TableCell>
                  <TableCell align="right">{provider?.stats?.quotes || 0}</TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={`${provider?.stats?.leadToQuoteRatio || 0}%`}
                      color={parseFloat(provider?.stats?.leadToQuoteRatio || 0) >= 25 ? 'success' : 
                             parseFloat(provider?.stats?.leadToQuoteRatio || 0) >= 15 ? 'warning' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">{provider?.stats?.sales || 0}</TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={`${provider?.stats?.conversionRate || 0}%`}
                      color={parseFloat(provider?.stats?.conversionRate || 0) > 20 ? 'success' : 
                             parseFloat(provider?.stats?.conversionRate || 0) > 10 ? 'warning' : 'default'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );

  // Submissions Tab Content
  const SubmissionsTab = () => {
    const columns = [
      {
        field: 'submission_reference',
        headerName: 'Reference',
        width: 200,
        renderCell: (params) => (
          <Typography variant="body2" fontFamily="monospace">
            {params.value}
          </Typography>
        )
      },
      {
        field: 'ServiceProvider',
        headerName: 'Provider',
        width: 150,
        renderCell: (params) => params.value?.name || 'N/A'
      },
      {
        field: 'Call',
        headerName: 'Phone',
        width: 130,
        renderCell: (params) => params.value?.phone || 'N/A'
      },
      {
        field: 'Agent',
        headerName: 'Agent ID',
        width: 120,
        renderCell: (params) => {
          const agent = params.value || params.row.Call?.agent;
          return agent ? (
            <Box>
              <Typography variant="body2" fontWeight="bold">
                {agent.username || agent.user || 'N/A'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                ID: {agent.id || 'N/A'}
              </Typography>
            </Box>
          ) : 'N/A';
        }
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        renderCell: (params) => (
          <Chip
            label={params.value}
            size="small"
            color={getStatusColor(params.value)}
            variant="outlined"
          />
        )
      },
      {
        field: 'feedback_status',
        headerName: 'Feedback',
        width: 120,
        renderCell: (params) => params.value ? (
          <Chip
            label={params.value}
            size="small"
            color={getFeedbackStatusColor(params.value)}
            variant="filled"
          />
        ) : (
          <Chip label="Pending" size="small" variant="outlined" />
        )
      },
      {
        field: 'quote_amount',
        headerName: 'Quote Amount',
        width: 120,
        renderCell: (params) => params.value ? `$${params.value.toLocaleString()}` : '-'
      },
      {
        field: 'sale_amount',
        headerName: 'Sale Amount',
        width: 120,
        renderCell: (params) => params.value ? `$${params.value.toLocaleString()}` : '-'
      },
      {
        field: 'sent_at',
        headerName: 'Submitted',
        width: 150,
        renderCell: (params) => {
          if (!params.value) return 'N/A';
          const date = new Date(params.value);
          if (isNaN(date.getTime())) return 'Invalid Date';
          
          // Format with proper timezone (South African timezone)
          return date.toLocaleString('en-ZA', {
            timeZone: 'Africa/Johannesburg',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          });
        }
      },
      {
        field: 'createdAt',
        headerName: 'Created', 
        width: 150,
        renderCell: (params) => {
          // Try different field names that might contain the creation timestamp
          const value = params.value || params.row.created_at || params.row.createdAt;
          if (!value) return 'N/A';
          
          const date = new Date(value);
          if (isNaN(date.getTime())) return 'Invalid Date';
          
          // Format with proper timezone (South African timezone)
          return date.toLocaleString('en-ZA', {
            timeZone: 'Africa/Johannesburg',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          });
        }
      }
    ];

    const CustomToolbar = () => (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarExport />
        <Button
          startIcon={<RefreshIcon />}
          onClick={fetchSubmissions}
          size="small"
        >
          Refresh
        </Button>
      </GridToolbarContainer>
    );

    return (
      <Box>
        {/* Advanced Filters */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Advanced Filters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Search"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="sent">Sent</MenuItem>
                  <MenuItem value="success">Success</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Feedback Status</InputLabel>
                <Select
                  value={filters.feedback_status}
                  onChange={(e) => handleFilterChange('feedback_status', e.target.value)}
                  label="Feedback Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="opened">OPENED</MenuItem>
                  <MenuItem value="qualified">Qualified</MenuItem>
                  <MenuItem value="converted">Converted</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                  <MenuItem value="contacted">Contacted</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Provider</InputLabel>
                <Select
                  value={filters.provider_id}
                  onChange={(e) => handleFilterChange('provider_id', e.target.value)}
                  label="Provider"
                >
                  <MenuItem value="">All</MenuItem>
                  {providers.map(provider => (
                    <MenuItem key={provider.id} value={provider.id}>
                      {provider.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          
          {/* Date Filter Row */}
          <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
            <Grid item xs={12} md={2}>
              <TextField
                label="From Date"
                type="date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="To Date"
                type="date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                onClick={() => {
                  const today = new Date().toISOString().split('T')[0];
                  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
                  handleFilterChange('date_from', thirtyDaysAgo);
                  handleFilterChange('date_to', today);
                }}
                variant="outlined"
                size="small"
                fullWidth
              >
                Last 30 Days
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button onClick={clearFilters} variant="outlined" size="small" fullWidth>
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Data Grid */}
        <Paper sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={submissions}
            columns={columns}
            loading={loading}
            paginationModel={{
              page: pagination.page,
              pageSize: pagination.pageSize
            }}
            onPaginationModelChange={(model) => setPagination(prev => ({
              ...prev,
              page: model.page,
              pageSize: model.pageSize
            }))}
            rowCount={pagination.total}
            paginationMode="server"
            pageSizeOptions={[10, 25, 50, 100]}
            slots={{
              toolbar: CustomToolbar
            }}
            disableRowSelectionOnClick
          />
        </Paper>
      </Box>
    );
  };

  // Performance Tab Content
  const PerformanceTab = () => {
    const getPeriodTitle = () => {
      switch (performancePeriod) {
        case 'daily':
          return 'Daily Performance Trends (Last 30 Days)';
        case 'weekly':
          return 'Weekly Performance Trends (Last 12 Weeks)';
        case 'monthly':
          return 'Monthly Performance Trends (Last 12 Months)';
        default:
          return 'Performance Trends';
      }
    };

    return (
      <Box>
        {/* Period Selection */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Time Period
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              variant={performancePeriod === 'daily' ? 'contained' : 'outlined'}
              onClick={() => setPerformancePeriod('daily')}
              size="small"
            >
              Daily
            </Button>
            <Button
              variant={performancePeriod === 'weekly' ? 'contained' : 'outlined'}
              onClick={() => setPerformancePeriod('weekly')}
              size="small"
            >
              Weekly
            </Button>
            <Button
              variant={performancePeriod === 'monthly' ? 'contained' : 'outlined'}
              onClick={() => setPerformancePeriod('monthly')}
              size="small"
            >
              Monthly
            </Button>
          </Box>
        </Paper>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {getPeriodTitle()}
              </Typography>
              {performanceData.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                  <Typography color="textSecondary">
                    {loading ? 'Loading performance data...' : 'No performance data available for the selected period'}
                  </Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={performanceData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Line type="monotone" dataKey="submissions" stroke="#8884d8" name="Total Submissions" />
                    <Line type="monotone" dataKey="successful" stroke="#82ca9d" name="Successful" />
                    <Line type="monotone" dataKey="conversions" stroke="#ff7300" name="Conversions" />
                  </LineChart>
                </ResponsiveContainer>
              )}
              {/* Debug info */}
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                Data points: {performanceData.length} | Period: {performancePeriod}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // OPENED Leads Resubmission Tab
  const OpenedLeadsTab = () => (
    <OpenedLeadsResubmission />
  );

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Lead Submission Analytics
        </Typography>
        <Box>
          <Button
            startIcon={<UploadIcon />}
            onClick={() => setUploadDialog(true)}
            variant="contained"
            color="primary"
            sx={{ mr: 1 }}
          >
            Upload Excel Report
          </Button>
          <Button
            startIcon={<DownloadIcon />}
            onClick={() => downloadReport('performance_summary')}
            variant="outlined"
          >
            Download Report
          </Button>
        </Box>
      </Box>

      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="Dashboard" icon={<AssessmentIcon />} />
        <Tab label="Submissions" icon={<ViewIcon />} />
        <Tab label="Performance" icon={<TrendingUpIcon />} />
        <Tab label="OPENED Leads" icon={<AssignmentReturnIcon />} />
      </Tabs>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {activeTab === 0 && <DashboardTab />}
      {activeTab === 1 && <SubmissionsTab />}
      {activeTab === 2 && <PerformanceTab />}
      {activeTab === 3 && <OpenedLeadsTab />}

      {/* Excel Upload Dialog */}
      <Dialog open={uploadDialog} onClose={() => setUploadDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Excel Report</DialogTitle>
        <DialogContent>
          {uploadResult && (
            <Alert 
              severity={uploadResult.success ? 'success' : 'error'} 
              sx={{ mb: 2 }}
            >
              {uploadResult.success ? (
                <div>
                  <strong>Upload Successful!</strong><br />
                  Rows processed: {uploadResult.data?.rowsProcessed}<br />
                  Matches found: {uploadResult.data?.matchesFound}<br />
                  New OPENED: {uploadResult.data?.openedInFile}<br />
                  New quotes: {uploadResult.data?.quotedInFile}<br />
                  New sales: {uploadResult.data?.salesInFile}<br />
                  Revenue added: ${uploadResult.data?.revenueAdded?.toLocaleString()}
                </div>
              ) : (
                uploadResult.error
              )}
            </Alert>
          )}
          
          <FormControl fullWidth margin="normal">
            <InputLabel>Service Provider</InputLabel>
            <Select
              value={uploadProvider}
              onChange={(e) => setUploadProvider(e.target.value)}
              label="Service Provider"
            >
              {providers.map(provider => (
                <MenuItem key={provider.id} value={provider.id}>
                  {provider.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box mt={2}>
            <input
              accept=".xlsx,.xls,.csv"
              style={{ display: 'none' }}
              id="excel-file-input"
              type="file"
              onChange={(e) => setUploadFile(e.target.files[0])}
            />
            <label htmlFor="excel-file-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                fullWidth
              >
                {uploadFile ? uploadFile.name : 'Choose Excel File'}
              </Button>
            </label>
          </Box>

          {uploadLoading && <LinearProgress sx={{ mt: 2 }} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialog(false)}>Cancel</Button>
          <Button
            onClick={handleExcelUpload}
            variant="contained"
            disabled={!uploadFile || !uploadProvider || uploadLoading}
          >
            Upload & Process
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeadSubmissions;